import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
  onSuccess?: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { login, error, clearError, isLoading } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    clearError();

    // Validation
    if (!email.trim()) {
      setValidationError(t('auth.errors.emailRequired'));
      return;
    }
    if (!email.includes('@')) {
      setValidationError(t('auth.errors.invalidEmail'));
      return;
    }
    if (!password) {
      setValidationError(t('auth.errors.passwordRequired'));
      return;
    }
    if (password.length < 3) {
      setValidationError(t('auth.errors.passwordTooShort'));
      return;
    }

    // Attempt login
    const success = await login(email.trim(), password);
    
    if (success) {
      // Reset form
      setEmail('');
      setPassword('');
      setValidationError('');
      
      // Close modal and call success callback
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setValidationError('');
    clearError();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSwitchToRegister = () => {
    setEmail('');
    setPassword('');
    setValidationError('');
    clearError();
    onSwitchToRegister();
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('auth.login')}</h3>
          <button className="modal-close" onClick={handleClose} disabled={isLoading}>
            ×
          </button>
        </div>

        <form className="modal-body auth-form" onSubmit={handleSubmit}>
          {(validationError || error) && (
            <div className="auth-error">
              {validationError || error}
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="login-email">{t('auth.email')}</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="auth-field">
            <label htmlFor="login-password">{t('auth.password')}</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('auth.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? t('auth.loggingIn') : t('auth.loginButton')}
            </button>
          </div>

          <div className="auth-switch">
            {t('auth.dontHaveAccount')}{' '}
            <button
              type="button"
              className="auth-switch-link"
              onClick={handleSwitchToRegister}
              disabled={isLoading}
            >
              {t('auth.registerLink')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
